import type { MobileCardConfiguration } from '../../types/list';

/**
 * Shared constants and filter rules for invoice lists
 */

/**
 * Base required fields for all invoice lists
 */
export const BASE_REQUIRED_FIELDS = [
	'_id',
	'externalId',
	'externalId2',
	'invoiceNumber',
	'date',
	'dueDate',
	'_fk_client__id',
	'fk_company__id',
	'clientName',
	'clientExternalId',
	'clientExternalId2',
	'clientAddress1',
	'clientAddress2',
	'clientCity',
	'clientState',
	'clientZip',
	'total',
	'companyName',
];

/**
 * Extended required fields for the "All" list (includes all column data)
 */
export const ALL_REQUIRED_FIELDS = [
	...BASE_REQUIRED_FIELDS,
	'numberOfLines',
	'tax',
	'freight',
	'discount',
	'subtotal',
	'salesRepFirstName',
	'm_sm_payment_outstandingBalance',
	'paymentStatus',
	'totalGP',
];

/**
 * Required fields for aging bucket lists (outstanding balance + payment status needed)
 */
export const AGING_REQUIRED_FIELDS = [
	...BASE_REQUIRED_FIELDS,
	'm_sm_payment_outstandingBalance',
	'paymentStatus',
];

/**
 * Base filter rules applied to all invoice lists
 */
export const BASE_FILTER_RULES = {
	invoiceSalesRep: {
		defaultValue: [{ id: 'ME', label: 'Me' }],
		applyDefaultIf: {
			type: 'group',
			joiner: 'AND',
			negated: false,
			children: [{ type: 'rule', action: 'invoices:view:any', negated: true }],
		},
		lockedIf: {
			type: 'group',
			joiner: 'AND',
			negated: false,
			children: [{ type: 'rule', action: 'invoices:view:any', negated: true }],
		},
		hiddenIf: {
			type: 'group',
			joiner: 'AND',
			negated: false,
			children: [{ type: 'rule', action: 'invoices:view:any', negated: true }],
		},
	},
	invoiceDateRange: {
		defaultValue: [
			{
				id: 'last-90-days',
				label: 'Last 90 Days',
				data: {
					startDate: '{{DATE:-90d}}',
					endDate: '{{DATE:0d}}',
				},
			},
		],
	},
};

/**
 * PD-13292: mobile card layout for the invoice lists, on the zones engine.
 * The Total renders as the hero metric in the card header (the figure the
 * list is scanned for), Amount Owed / Date / Sales Rep / Lines flow as
 * inline meta chips, the client block renders full-width, and the payment
 * badge sits in the footer. Subtotal, adjustments (tax/freight/discount)
 * and GP stay desktop-only — on a phone they're noise next to Total +
 * Amount Owed.
 */
export const INVOICE_MOBILE_CARD_CONFIG: MobileCardConfiguration = {
	cardLayout: 'zones',
	primaryFields: ['invoice-number'],
	heroField: 'invoice-totals.total-field',
	wideFields: ['client-info'],
	hiddenFields: ['invoice-totals.subtotal-field', 'adjustments', 'gp-totals'],
	// "Sales Rep" eats half the meta column's width before the name even
	// starts; "Rep" keeps most names on one line (review note).
	labelOverrides: { salesperson: 'Rep' },
};
